import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/components/ui/Dialog";
import { useSupport } from "../hooks/useSupport";
import { Button } from "./ui/Button";
import { Loader2Icon } from "lucide-react";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Textarea } from "./ui/Textarea";
import { Input } from "./ui/Input";
import { Label } from "./ui/Label";
import { Form } from "./ui/Form";
import { AppSupportPayload } from "@/types/AppSupport";
import { showValidationErrors } from "../lib/useAppFormValidator";
import { ApiErrorResponse } from "@/types/ApiResponse";

const usPhoneDigitsRegex = /^(?:1)?[2-9]\d{2}[2-9]\d{6}$/;
const supportFormSchema = z.object({
	name: z.string().nonempty("Required.").default(""),
	email: z.email({ message: "Please enter a valid email address" }).default(""),
	phone: z
		.string()
		.trim()
		.refine(value => value === "" || /^\d+$/.test(value), {
			message: "Phone number must contain digits only",
		})
		.refine(value => value === "" || usPhoneDigitsRegex.test(value), {
			message: "Please enter a valid US phone number",
		})
		.optional()
		.transform(value => (value === "" ? undefined : value)),
	subject: z.string().nonempty("Required.").default(""),
	message: z.string().nonempty("Required.").default(""),
});

export type SupportFormValues = z.input<typeof supportFormSchema>;

export function Support() {
	const { openSupport, openThanks, triggerSupportClose, triggerThanksOpen, supportApi } = useSupport();
	const { createSupportTicket, isLoading, isSuccess } = supportApi;

	const form = useForm<SupportFormValues>({
		resolver: zodResolver(supportFormSchema),
	});

	const onSubmit = async () => {
		try {
			await createSupportTicket(form.getValues() as AppSupportPayload).unwrap();
			form.reset();
			triggerThanksOpen();
		} catch (e) {
			showValidationErrors<SupportFormValues>({
				form,
				error: e as ApiErrorResponse,
				defaultMessage: "Support Ticket Creation Failed. Please try again.",
			});
		}
	};

	return (
		<Dialog open={openSupport()} onOpenChange={triggerSupportClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle className="mb-3">Client Services Assistance</DialogTitle>
					{!isSuccess && (
						<DialogDescription>
							Please complete the form below and a member of client services will contact you within one business day.
						</DialogDescription>
					)}
				</DialogHeader>
				{!openThanks() && (
					<div>
						<Form {...form}>
							<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
								<div className="flex flex-col gap-3">
									<div>
										<Label htmlFor="name" className="text-sm">
											Name
										</Label>
										<Input type="text" id="name" {...form.register("name")} />
										{form.formState.errors.name && (
											<p className="mt-1 text-sm text-red-500">{form.formState.errors.name.message}</p>
										)}
									</div>
									<div>
										<Label htmlFor="email" className="text-sm">
											Email
										</Label>
										<Input type="email" id="email" {...form.register("email")} />
										{form.formState.errors.email && (
											<p className="mt-1 text-sm text-red-500">{form.formState.errors.email.message}</p>
										)}
									</div>
									<div>
										<Label htmlFor="phone" className="text-sm">
											Phone
										</Label>
										<Input type="phone" id="phone" {...form.register("phone")} />
										{form.formState.errors.phone && (
											<p className="mt-1 text-sm text-red-500">{form.formState.errors.phone.message}</p>
										)}
									</div>
									<div>
										<Label htmlFor="subject" className="text-sm">
											Subject
										</Label>
										<Input type="subject" id="subject" {...form.register("subject")} />
										{form.formState.errors.subject && (
											<p className="mt-1 text-sm text-red-500">{form.formState.errors.subject.message}</p>
										)}
									</div>
									<div>
										<label htmlFor="message" className="-sm">
											Message
										</label>
										<Textarea
											id="message"
											placeholder="Enter your message here..."
											{...form.register("message")}
											className="mt-1 w-full"
										/>
										{form.formState.errors.message && (
											<p className="text-sm text-red-600">{form.formState.errors.message.message}</p>
										)}
									</div>
								</div>
								<DialogFooter>
									<Button disabled={isLoading} variant="success">
										Send
										{isLoading && <Loader2Icon className="ml-2 animate-spin" />}
									</Button>
								</DialogFooter>
							</form>
						</Form>
					</div>
				)}
				{openThanks() && (
					<div>
						<p>Thank you! We have received your request and will contact you shortly.</p>
						<p className="mt-2 text-center">
							<Button type="button" disabled={isLoading} variant="destructive" onClick={triggerSupportClose}>
								Close
							</Button>
						</p>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
